import { motion } from 'framer-motion';
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { Factory, Building, Building2, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Products = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const products = [
    {
      category: '5-25 kVA',
      title: 'Small Capacity Generators',
      image: 'https://images.unsplash.com/photo-1683119926853-6427b2966f9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxsYXJnZSUyMGluZHVzdHJpYWwlMjBkaWVzZWwlMjBnZW5lcmF0b3IlMjBtYWNoaW5lJTIwaXNvbGF0ZWR8ZW58MHx8fHwxNzcxOTEzMjU1fDA&ixlib=rb-4.1.0&q=85',
      description: 'Ideal for residential properties, small offices, retail stores, and small-scale commercial establishments requiring reliable backup power.',
      applications: [
        'Residential Homes',
        'Small Retail Stores',
        'Clinics and Medical Centers',
        'Small Offices',
        'Restaurants and Cafes',
      ],
      features: [
        'Compact and space-efficient design',
        'Low noise operation',
        'Fuel-efficient performance',
        'Easy installation and maintenance',
        'Cost-effective solution',
      ],
      icon: <Building2 className="w-8 h-8" />,
    },
    {
      category: '30-100 kVA',
      title: 'Medium Capacity Generators',
      image: 'https://images.unsplash.com/photo-1704747081666-f4fd33d5db5c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxsYXJnZSUyMGluZHVzdHJpYWwlMjBkaWVzZWwlMjBnZW5lcmF0b3IlMjBtYWNoaW5lJTIwaXNvbGF0ZWR8ZW58MHx8fHwxNzcxOTEzMjU1fDA&ixlib=rb-4.1.0&q=85',
      description: 'Perfect for apartment complexes, medium-sized commercial buildings, and offices requiring dependable power backup solutions.',
      applications: [
        'Apartment Complexes',
        'Shopping Malls',
        'Corporate Offices',
        'Educational Institutions',
        'Hotels and Resorts',
      ],
      features: [
        'Robust and reliable performance',
        'Advanced control systems',
        'Automatic load management',
        'Extended runtime capability',
        'Weather-resistant enclosures',
      ],
      icon: <Building className="w-8 h-8" />,
    },
    {
      category: '125-250 kVA',
      title: 'High Capacity Generators',
      image: 'https://images.unsplash.com/photo-1765897569589-effcccec14d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwZGllc2VsJTIwZ2VuZXJhdG9yJTIwcG93ZXIlMjBwbGFudCUyMHNpdGV8ZW58MHx8fHwxNzcxOTEzMjM2fDA&ixlib=rb-4.1.0&q=85',
      description: 'Designed for large industrial facilities, hospitals, and data centers that demand uninterrupted power supply for critical operations.',
      applications: [
        'Manufacturing Plants',
        'Hospitals and Healthcare Facilities',
        'Data Centers',
        'Large Construction Sites',
        'Telecommunication Towers',
      ],
      features: [
        'Heavy-duty industrial grade',
        'High fuel efficiency',
        'Advanced monitoring systems',
        'Multiple safety features',
        'Long operational life',
      ],
      icon: <Factory className="w-8 h-8" />,
    },
  ];

  const handleRequestQuote = (category) => {
    setSelectedCategory(category);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/quote`, {
        ...formData,
        category: selectedCategory,
      });

      toast.success('Quote request submitted successfully! We will contact you soon.');
      setIsDialogOpen(false);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Failed to submit quote request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20" data-testid="products-page">
      <section className="py-20 bg-gradient-to-br from-[#1B365D] to-[#2563eb] text-white noise-overlay" data-testid="products-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="products-title">
              Our Generator Range
            </h1>
            <p className="text-lg sm:text-xl text-slate-200 max-w-3xl mx-auto">
              High-quality diesel generators from 5 kVA to 250 kVA for all your power needs
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-slate-50" data-testid="products-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {products.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`bg-white rounded-sm border border-slate-200 shadow-lg overflow-hidden ${
                  index % 2 === 0 ? '' : 'lg:flex-row-reverse'
                }`}
                data-testid={`product-card-${index}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="relative h-80 lg:h-auto overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-6 left-6 bg-[#F97316] text-white px-6 py-3 rounded-sm">
                      <div className="flex items-center gap-2">
                        {product.icon}
                        <span className="text-xl font-bold">{product.category}</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-8 lg:p-12">
                    <h2 className="text-3xl font-bold text-[#1B365D] mb-4" data-testid={`product-title-${index}`}>
                      {product.title}
                    </h2>
                    <p className="text-slate-600 mb-6 leading-relaxed">{product.description}</p>

                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-[#1B365D] mb-3">Applications:</h3>
                      <ul className="space-y-2">
                        {product.applications.map((app, appIndex) => (
                          <li key={appIndex} className="flex items-center gap-2 text-slate-600">
                            <CheckCircle className="w-5 h-5 text-[#F97316] flex-shrink-0" />
                            {app}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-[#1B365D] mb-3">Key Features:</h3>
                      <ul className="space-y-2">
                        {product.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center gap-2 text-slate-600">
                            <CheckCircle className="w-5 h-5 text-[#F97316] flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <button
                      onClick={() => handleRequestQuote(product.category)}
                      className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold py-3 px-8 rounded-sm transition-all uppercase tracking-wide"
                      data-testid={`request-quote-btn-${index}`}
                    >
                      Request Quote
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle>Request a Quote - {selectedCategory}</DialogTitle>
            <DialogDescription>
              Fill in your details and we'll get back to you with a customized quote.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                data-testid="quote-form-name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter your name"
              />
            </div>
            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                data-testid="quote-form-email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your.email@example.com"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                data-testid="quote-form-phone"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+91 XXXXXXXXXX"
              />
            </div>
            <div>
              <Label htmlFor="message">Additional Requirements (Optional)</Label>
              <Textarea
                id="message"
                data-testid="quote-form-message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Tell us about your specific requirements..."
                rows={4}
              />
            </div>
            <Button
              type="submit"
              data-testid="quote-form-submit"
              disabled={isSubmitting}
              className="w-full bg-[#F97316] hover:bg-[#EA580C]"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};